import React, { Component } from 'react';
import { Link } from 'react-router-dom';
import numberFormatter from 'number-formatter';
import moment from 'moment';
import Paginator from './Paginator';
import PropTypes from 'prop-types';
import LoadingScreen from '../LoadingScreen';
import clientServices from '../../services/client';
import { getActiveStatus } from '../../utils/enrolleeStatus';

import IconButton from '@mui/material/IconButton';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import ListItemIcon from '@mui/material/ListItemIcon';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import { withTranslation } from 'react-i18next';

class DataTable extends Component {
    constructor(props) {
        super(props);
        this.state = {
            isLoading: false,
            tableData: [],
            filter: '',
            pagination: null,
            anchorEl: null,
            sortBy: '',
            selectedElement: null,
            selectedRow: null
        };
    }

    componentDidMount() {
        this.getTableData();
    }

    componentDidUpdate(prevProps) {
        if (
            this.props.dataSource !== prevProps.dataSource ||
            this.props.fetchData !== prevProps.fetchData
        ) {
            this.getTableData();
        }
    }

    getTableData = (page = 1, filter = '', limit = 10) => {
        const { dataSource, tableData, fetchData } = this.props;

        if (fetchData) {
            this.setLoading(true);

            return fetchData(page, filter, limit)
                .then((response) =>
                    this.setState({
                        isLoading: false,
                        tableData: response.data,
                        filter,
                        pagination: response.pagination
                    })
                )
                .catch((error) => {
                    this.setLoading(false);
                });
        }

        const { accessToken, clientId, ...queryProps } = dataSource || {};
        const queryData = { limit, page, q: filter, ...queryProps };

        if (dataSource) {
            this.setLoading(true);

            clientServices
                .getStaff(accessToken, clientId, queryData)
                .then((response) =>
                    this.setState({
                        isLoading: false,
                        tableData: response.data,
                        filter,
                        pagination: response.pagination
                    })
                )
                .catch((error) => {
                    this.setLoading(false);
                });
        } else {
            this.setState({
                tableData
            });
        }
    };

    setLoading = (isLoading) => {
        this.setState({
            isLoading
        });
    };

    filterList = (query) => {
        if (query.length > 3) {
            this.getTableData(1, query);
        }
    };

    clearFilter = () => {
        this.getTableData();
    };

    formatDataBasedOnType = (data, type = 'string') => {
        const { t } = this.props;
        switch (type) {
            case 'string':
                return data;
            case 'number':
                return t("common.amount", { amount: Number(data)});
            case 'date':
                return t('date', {
                    date: data,
                    formatParams: {
                        date: {
                            weekday: 'long',
                            year: 'numeric',
                            month: 'long',
                            day: 'numeric'
                        }
                    }
                });
            default:
                return data;
        }
    };

    handleClick = (event,data ) => {
        this.setState({
            anchorEl: event.currentTarget,
            selectedElement: data.id,
            selectedRow: data
        });
    };

    handleClose = () => {
        this.setState({
            anchorEl: null
        });
    };

    render() {
        const {
            tableColumnData,
            showTableDetail,
            options,
            emptyStateMessage,
            tableClassName,
            menuSx,
            t
        } = this.props;
        const {
            tableData,
            isLoading,
            filter,
            pagination,
            anchorEl,
            sortBy,
            selectedElement,
            selectedRow
        } = this.state;
        const sortedTableData = this.state.tableData.sort((a, b) => {
            if (typeof a[`${sortBy}`] === 'string') {
                return a[`${sortBy}`].localeCompare(b[`${sortBy}`]); // String comparison
            } else if (typeof a[`${sortBy}`] === 'number') {
                return a[`${sortBy}`] - b[`${sortBy}`]; // Number comparison
            } else {
                return typeof a === 'number' ? -1 : 1; // Numbers come before strings
            }
        });
        if (isLoading)
            return (
                <div
                    className="dashboard-card"
                    style={{ minHeight: '20rem', position: 'relative' }}
                >
                    <LoadingScreen />
                </div>
            );

        return (
            <div className="dashboard-table-overflow">
                {tableData.length === 0 ? (
                    <div
                        className="dashboard-card"
                        style={{
                            width: '20%',
                            margin: '20px auto',
                            fontSize: '20px',
                            color: 'grey'
                        }}
                    >
                        {emptyStateMessage || t("employees.emptyTableMessage")}
                    </div>
                ) : (
                    <div>
                        <table className={`dashboard__table ${tableClassName || ''}`.trim()}>
                            <thead>
                                <tr>
                                    {tableColumnData.map((columnData) => (
                                        <th
                                            onClick={() =>
                                                this.setState({
                                                    sortBy: columnData.key
                                                })
                                            }
                                            key={columnData.key || columnData.alias}
                                            className="dashboard-table-item dashboard-table-item__header"
                                        >
                                            {columnData.alias
                                                ? columnData.alias
                                                : columnData.key.replace(/_/g, ' ')}
                                        </th>
                                    ))}
                                    {
                                        <th
                                            key="context-meun"
                                            className="dashboard-table-item dashboard-table-item__header"
                                        ></th>
                                    }
                                </tr>
                            </thead>
                            <tbody>
                                {sortedTableData.map((data, index) => (
                                    <tr
                                        key={`${data.id}-${index}`}
                                        className={getActiveStatus(data.active_status_id)}
                                        onClick={() => showTableDetail && showTableDetail(data.id)}
                                    >
                                        {tableColumnData.map((colData, index) => (
                                            <td
                                                key={index + 1}
                                                className="dashboard-table-item dashboard-table-item__body"
                                            >
                                                {colData.format
                                                    ? colData.format(data)
                                                    : !data[colData.key]
                                                    ? 'N/A'
                                                    : this.formatDataBasedOnType(
                                                          data[colData.key || colData],
                                                          colData.type
                                                      )}
                                            </td>
                                        ))}
                                        {options && (
                                            <td>
                                                <div>
                                                    <IconButton
                                                        aria-label="more"
                                                        id="long-button"
                                                        aria-controls={
                                                            Boolean(anchorEl)
                                                                ? 'long-menu'
                                                                : undefined
                                                        }
                                                        aria-expanded={
                                                            Boolean(anchorEl) ? 'true' : undefined
                                                        }
                                                        aria-haspopup="true"
                                                        onClick={(event) => this.handleClick(event, data)}
                                                    >
                                                        <MoreVertIcon />
                                                    </IconButton>
                                                    <Menu
                                                        id="long-menu"
                                                        MenuListProps={{
                                                            'aria-labelledby': 'long-button'
                                                        }}
                                                        anchorEl={anchorEl}
                                                        open={Boolean(anchorEl)}
                                                        onClose={this.handleClose}
                                                        slotProps={{
                                                            paper: {
                                                                sx: [
                                                                    {
                                                                        maxHeight: 40 * 4.5,
                                                                        minWidth: '20ch'
                                                                    },
                                                                    menuSx
                                                                ]
                                                            }
                                                        }}
                                                    >
                                                        {options.map((option) => (
                                                            <MenuItem
                                                                key={option.value}
                                                                disabled={
                                                                    option.value === 'Renew Plan' ||
                                                                    option.value === 'Remove Employee'
                                                                }
                                                                onClick={() => {
                                                                    if (option.onClick)
                                                                        option.onClick(
                                                                            selectedElement,
                                                                            selectedRow
                                                                        );
                                                                    this.handleClose();
                                                                }}
                                                            >
                                                              {option.icon && (
                                                                <ListItemIcon>{option.icon}</ListItemIcon>
                                                              )}
                                                              {option.value === 'View Employee' ? (
                                                                <Link style={{textDecoration: 'none', color: 'black'}} to={`/dashboard/staff/${selectedElement}`}>{option.label}</Link>
                                                                ) :
                                                                option.label
                                                              }
                                                            </MenuItem>
                                                        ))}
                                                    </Menu>
                                                </div>
                                            </td>
                                        )}
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                        {pagination && (
                            <Paginator
                                lastPage={pagination.last_page}
                                currentPage={pagination.current_page}
                                onPageChange={(page) => {
                                    this.getTableData(page, filter || '');
                                }}
                            />
                        )}
                    </div>
                )}
            </div>
        );
    }
}

DataTable.propTypes = {
    tableData: PropTypes.array,
    tableColumnData: PropTypes.arrayOf(
        PropTypes.shape({
            key: PropTypes.string.isRequired,
            type: PropTypes.string.isRequired,
            alias: PropTypes.string,
            format: PropTypes.func
        })
    ),
    showTableDetail: PropTypes.func,
    styles: PropTypes.object,
    styleClass: PropTypes.string,
    dataSource: PropTypes.shape({
        // url:PropTypes.string,
        clientId: PropTypes.number,
        accessToken: PropTypes.string
    }),
    fetchData: PropTypes.func,
    emptyStateMessage: PropTypes.node,
    tableClassName: PropTypes.string,
    menuSx: PropTypes.oneOfType([PropTypes.object, PropTypes.array, PropTypes.func]),
    options: PropTypes.arrayOf(PropTypes.shape({
        label: PropTypes.string,
        value: PropTypes.string,
        icon: PropTypes.node,
        onClick: PropTypes.func
    }))
};

export default withTranslation()(DataTable);
